// -----------------------------------------------------------------------------
// Accordion: animates <details> rows open and closed (height, with the content
// fading in) instead of snapping. JS takes over the native name="" exclusivity
// so the row that closes animates too. Used by the FAQ and Feature Panels blocks.
//
// accordion(container, { keepOne }) — rows are the container's <details> children.
// keepOne: one row always stays open (clicking the open row does nothing).
// -----------------------------------------------------------------------------

const DURATION = 400;
const EASE = 'cubic-bezier(0.4, 0, 0.2, 1)';
const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

// A row mid-animation counts as where it's heading
const isOpen = (row) => {
  if (row.dataset.target) {
    return row.dataset.target === 'open';
  }

  return row.open;
};

const setOpen = (row, open) => {
  const summary = row.querySelector('summary');
  const content = summary.nextElementSibling;
  const start = row.offsetHeight;

  row.getAnimations().forEach((animation) => animation.cancel());
  content.getAnimations().forEach((animation) => animation.cancel());

  if (reduceMotion()) {
    row.open = open;
    return;
  }

  row.dataset.target = open ? 'open' : 'closed';
  row.open = true;

  const end = open ? row.offsetHeight : summary.offsetHeight + (row.offsetHeight - row.clientHeight);
  const offset = ['translateY(-0.5rem)', 'none'];

  row.style.overflow = 'hidden';
  row.animate({
    height: [`${start}px`, `${end}px`]
  }, {
    duration: DURATION,
    easing: EASE
  }).onfinish = () => {
    if (!open) {
      row.open = false;
    }

    row.style.overflow = '';
    delete row.dataset.target;
    content.getAnimations().forEach((animation) => animation.cancel());
  };

  content.animate({
    opacity: open ? [0, 1] : [1, 0],
    transform: open ? offset : offset.slice().reverse()
  }, {
    duration: DURATION,
    easing: EASE,
    fill: 'forwards'
  });
};

export default (container, { keepOne = false } = {}) => {
  const rows = [...container.querySelectorAll(':scope > details')];

  rows.forEach((row) => {
    row.removeAttribute('name');
    row.querySelector('summary').addEventListener('click', (event) => {
      event.preventDefault();

      if (isOpen(row)) {
        if (!keepOne) {
          setOpen(row, false);
        }

        return;
      }

      rows.filter((other) => other !== row && isOpen(other)).forEach((other) => setOpen(other, false));
      setOpen(row, true);
    });
  });
};
